import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  BookOpenIcon,
  ClockIcon,
  StarIcon,
  UserIcon,
  CheckCircleIcon,
  LockClosedIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  BeakerIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { sampleCourse } from '../../utils/sampleCourseData';
import courseService from '../../services/courseService';
import ContentViewer from '../../components/course/ContentViewer';
import QuizComponent from '../../components/course/QuizComponent';
import CourseRating from '../../components/course/CourseRating';
import AssignmentComponent from '../../components/course/AssignmentComponent';

interface CourseProgress {
  moduleId: string;
  contentId: string;
  completed: boolean;
  score?: number;
  completedAt?: Date;
}

interface UserEnrollment {
  courseId: string;
  enrolledAt: Date;
  progress: CourseProgress[];
  overallProgress: number;
  currentModule: string;
  certificateEarned?: boolean;
  quizScores?: Record<string, number>;
  status?: string;
}

// Countdown component for real-time cooldown display
const SidebarCooldownTimer: React.FC<{ endTime: Date }> = ({ endTime }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((endTime.getTime() - now.getTime()) / 1000));
      setTimeLeft(remaining);
      
      if (remaining <= 0) {
        return false; // Stop timer
      }
      return true;
    };
    
    // Initial update
    if (!updateTimer()) return;
    
    const interval = setInterval(() => {
      if (!updateTimer()) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [endTime]);
  
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  
  return (
    <span className="text-xs text-red-500">
      Cooldown: {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  );
};

const CourseDetailsPage: React.FC = () => {
  const { id: courseId } = useParams<{ id: string }>();
  const { userProfile } = useAuth();
  const [course, setCourse] = useState<any>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollment, setEnrollment] = useState<UserEnrollment | null>(null);
  const [activeModule, setActiveModule] = useState<string>('');
  const [activeContent, setActiveContent] = useState<string>('');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [currentViewMode, setCurrentViewMode] = useState<'overview' | 'content' | 'quiz' | 'assignment' | 'rating'>('overview');
  const [currentContentData, setCurrentContentData] = useState<any>(null);

  useEffect(() => {
    console.log('useEffect triggered with courseId:', courseId);
    if (courseId) {
      console.log('Calling fetchCourseData...');
      fetchCourseData();
    } else {
      console.log('No courseId found');
      setLoading(false);
    }
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      console.log('Fetching course data for courseId:', courseId);
      
      let courseData = null;
      
      // For development/demo purposes, if courseId matches sample course, use it directly
      if (courseId === sampleCourse.id) {
        console.log('Using sample course data directly');
        courseData = sampleCourse;
      } else {
        // Try to fetch real course from API
        try {
          console.log('Attempting to fetch from API...');
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses/${courseId}`);
          console.log('API response status:', response.status);
          
          if (response.ok) {
            const result = await response.json();
            console.log('API response result:', result);
            courseData = result.data;
          } else {
            console.log('API response not ok:', response.status, response.statusText);
          }
        } catch (error) {
          console.log('Error fetching from API:', error);
          console.log('Backend might not be running');
        }
      }
      
      // If still no course data, show error
      if (!courseData) {
        console.log('No course data found');
        toast.error(`Course with ID "${courseId}" not found. The backend might not be running or the course doesn't exist.`);
        return;
      }
      
      console.log('Setting course data:', courseData);
      setCourse(courseData);
      
      // Check enrollment status if we have an auth token and course data
      const token = localStorage.getItem('authToken');
      if (token && courseData) {
        console.log('Checking enrollment status for courseId:', courseId);
        console.log('User profile exists:', !!userProfile);
        try {
          const enrollmentResponse = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/enrollment/${courseId}`, 
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Enrollment response status:', enrollmentResponse.status);
          
          if (enrollmentResponse.ok) {
            const enrollmentResult = await enrollmentResponse.json();
            console.log('Full enrollment API response:', JSON.stringify(enrollmentResult, null, 2));
            
            if (enrollmentResult.success && enrollmentResult.data.enrolled) {
              console.log('✅ User IS enrolled! Setting up enrollment data...');
              setIsEnrolled(true);
              setEnrollment({
                courseId: enrollmentResult.data.courseId,
                enrolledAt: new Date(enrollmentResult.data.enrolledAt),
                overallProgress: enrollmentResult.data.overallProgress || (typeof enrollmentResult.data.progress === 'number' ? enrollmentResult.data.progress : 0),
                currentModule: enrollmentResult.data.currentModule || courseData.modules[0]?.id || '',
                progress: Array.isArray(enrollmentResult.data.progress) 
                  ? enrollmentResult.data.progress 
                  : enrollmentResult.data.completedContent?.map((item: string) => {
                      const [moduleId, contentId] = item.split(':');
                      return {
                        moduleId,
                        contentId,
                        completed: true,
                        completedAt: new Date()
                      };
                    }) || [],
                quizScores: enrollmentResult.data.quizScores || {},
                status: enrollmentResult.data.status || 'in_progress',
                certificateEarned: enrollmentResult.data.certificateEarned || false
              });
              
              // Set active content
              if (courseData.modules.length > 0) {
                setActiveModule(courseData.modules[0].id);
                if (Array.isArray(courseData.modules[0].content) && courseData.modules[0].content.length > 0) {
                  setActiveContent((courseData.modules[0].content as any[])[0].id);
                } else if (courseData.modules[0].content && typeof courseData.modules[0].content === 'object' && courseData.modules[0].content.type) {
                  const contentId = courseData.modules[0].content.type === 'lesson' ? `${courseData.modules[0].id}-lesson` : courseData.modules[0].id;
                  setActiveContent(contentId);
                }
                setExpandedModules(new Set([courseData.modules[0].id]));
              }
            } else {
              console.log('❌ User is NOT enrolled');
              setIsEnrolled(false);
              setEnrollment(null);
            }
          } else {
            console.log('❌ Enrollment check failed with status:', enrollmentResponse.status);
            setIsEnrolled(false);
            setEnrollment(null);
          }
        } catch (error) {
          console.error('❌ Error checking enrollment:', error);
          setIsEnrolled(false);
          setEnrollment(null);
        }
      } else {
        console.log('No auth token or course data, skipping enrollment check');
        console.log('Token exists:', !!token);
        console.log('Course data exists:', !!courseData);
        setIsEnrolled(false);
        setEnrollment(null);
      }
      
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      // Always stop loading, regardless of success or failure
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const enrollInCourse = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to enroll in courses');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/enroll/${courseId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing enrollment response:', parseError);
        result = { success: false, message: 'Invalid response from server' };
      }
      
      console.log('Enrollment response:', { status: response.status, result });
      
      if (response.ok || response.status === 409) {
        // Check if already enrolled or successfully enrolled
        if (response.status === 409 || result.message?.includes('already enrolled')) {
          toast.success('You are already enrolled in this course');
        } else if (result.success || response.ok) {
          toast.success('Successfully enrolled in course!');
        }
        
        // Always set enrolled state and update UI
        setIsEnrolled(true);
        
        // Set up initial course navigation
        if (course.modules.length > 0) {
          setActiveModule(course.modules[0].id);
          if (Array.isArray(course.modules[0].content) && course.modules[0].content.length > 0) {
            setActiveContent((course.modules[0].content as any[])[0].id);
          } else if (course.modules[0].content && typeof course.modules[0].content === 'object' && course.modules[0].content.type) {
            const contentId = course.modules[0].content.type === 'lesson' ? `${course.modules[0].id}-lesson` : course.modules[0].id;
            setActiveContent(contentId);
          }
          setExpandedModules(new Set([course.modules[0].id]));
        }
        
        // Refresh enrollment data
        await fetchCourseData();
      } else {
        // For 500 errors, the enrollment might have succeeded despite the error
        if (response.status === 500) {
          console.log('Got 500 error, but enrollment might have succeeded. Checking status...');
          const checkingToast = toast.loading('Verifying enrollment status...');
          
          setTimeout(async () => {
            try {
              const token = localStorage.getItem('authToken');
              if (token) {
                const checkResponse = await fetch(
                  `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/enrollment/${courseId}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  }
                );
                
                if (checkResponse.ok) {
                  const checkResult = await checkResponse.json();
                  if (checkResult.success && checkResult.data.enrolled) {
                    console.log('Enrollment succeeded despite 500 error from enrollment API');
                    toast.dismiss(checkingToast);
                    toast.success('Successfully enrolled in course!');
                    setIsEnrolled(true);
                    
                    // Set up initial course navigation
                    if (course.modules.length > 0) {
                      setActiveModule(course.modules[0].id);
                      if (Array.isArray(course.modules[0].content) && course.modules[0].content.length > 0) {
                        setActiveContent((course.modules[0].content as any[])[0].id);
                      } else if (course.modules[0].content && typeof course.modules[0].content === 'object' && course.modules[0].content.type) {
                        const contentId = course.modules[0].content.type === 'lesson' ? `${course.modules[0].id}-lesson` : course.modules[0].id;
                        setActiveContent(contentId);
                      }
                      setExpandedModules(new Set([course.modules[0].id]));
                    }
                    
                    await fetchCourseData();
                    return;
                  }
                }
              }
            } catch (checkError) {
              console.error('Error checking enrollment status after 500:', checkError);
            }
            
            // If we get here, enrollment actually failed
            toast.dismiss(checkingToast);
            toast.error(result.message || 'Failed to enroll in course due to server error. Please try again.');
          }, 2000);
        } else {
          // For other errors, show error immediately
          console.error('Enrollment failed:', { status: response.status, result });
          toast.error(result.message || `Failed to enroll in course (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      
      // Don't show error immediately, check enrollment status first
      console.log('Network error occurred, checking enrollment status...');
      
      // Show a loading toast while checking
      const checkingToast = toast.loading('Verifying enrollment status...');
      
      // Wait a moment then check enrollment status
      setTimeout(async () => {
        try {
          const token = localStorage.getItem('authToken');
          if (token) {
            const checkResponse = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/enrollment/${courseId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            if (checkResponse.ok) {
              const checkResult = await checkResponse.json();
              if (checkResult.success && checkResult.data.enrolled) {
                console.log('Enrollment actually succeeded despite 500 error');
                toast.dismiss(checkingToast);
                toast.success('Successfully enrolled in course!');
                setIsEnrolled(true);
                
                // Set up initial course navigation
                if (course.modules.length > 0) {
                  setActiveModule(course.modules[0].id);
                  if (Array.isArray(course.modules[0].content) && course.modules[0].content.length > 0) {
                    setActiveContent((course.modules[0].content as any[])[0].id);
                  } else if (course.modules[0].content && typeof course.modules[0].content === 'object' && course.modules[0].content.type) {
                    const contentId = course.modules[0].content.type === 'lesson' ? `${course.modules[0].id}-lesson` : course.modules[0].id;
                    setActiveContent(contentId);
                  }
                  setExpandedModules(new Set([course.modules[0].id]));
                }
                
                await fetchCourseData();
                return;
              }
            }
          }
        } catch (checkError) {
          console.error('Error checking enrollment status:', checkError);
        }
        
        // If we get here, enrollment actually failed
        toast.dismiss(checkingToast);
        toast.error('Failed to enroll in course. The backend may be experiencing issues. Please try again in a moment.');
      }, 2000); // Wait 2 seconds to give backend time to process
    }
  };

  const markContentComplete = async (moduleId: string, contentId: string) => {
    if (!enrollment) return;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to track progress');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/progress/${courseId}/${moduleId}/${contentId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ completed: true })
        }
      );

      const result = await response.json();
      
      if (response.ok && result.success) {
        // Update local state
        const newProgress = enrollment && Array.isArray(enrollment.progress) ? [...enrollment.progress] : [];
        const existingIndex = newProgress.findIndex(p => p.moduleId === moduleId && p.contentId === contentId);
        
        if (existingIndex >= 0) {
          newProgress[existingIndex].completed = true;
          newProgress[existingIndex].completedAt = new Date();
        } else {
          newProgress.push({
            moduleId,
            contentId,
            completed: true,
            completedAt: new Date()
          });
        }
        
        setEnrollment({
          ...enrollment,
          progress: newProgress,
          overallProgress: result.data.progress || enrollment.overallProgress
        });
        
        toast.success('Content marked as complete!');
      } else {
        toast.error(result.message || 'Failed to update progress');
      }
    } catch (error) {
      console.error('Error marking content complete:', error);
      toast.error('Failed to update progress');
    }
  };

  const isContentCompleted = (moduleId: string, contentId: string): boolean => {
    return enrollment && Array.isArray(enrollment.progress) && enrollment.progress.some(p => p.moduleId === moduleId && p.contentId === contentId && p.completed) || false;
  };

  // Helper function to get content count from module
  const getContentCount = (module: any): number => {
    if (Array.isArray(module.content)) {
      return module.content.length;
    } else if (module.content && typeof module.content === 'object' && module.content.type) {
      return 1; // Single content object
    }
    return 0;
  };

  // Helper function to check if all content in a module is completed
  const isModuleCompleted = (module: any): boolean => {
    if (Array.isArray(module.content)) {
      return module.content.every((content: any) => 
        enrollment && Array.isArray(enrollment.progress) && 
        enrollment.progress.some((p: any) => p.contentId === content.id && p.completed)
      );
    } else if (module.content && typeof module.content === 'object' && module.content.type) {
      const contentId = module.content.type === 'lesson' ? `${module.id}-lesson` : module.id;
      return isContentCompleted(module.id, contentId);
    }
    return false;
  };

  const canAccessContent = (moduleId: string, contentIndex: number): boolean => {
    if (!isEnrolled) return false;
    
    const module = course.modules.find((m: any) => m.id === moduleId);
    if (!module) return false;
    
    // Can access first content of first module
    if (module.order === 1 && contentIndex === 0) return true;
    
    // Can access if previous content is completed
    if (contentIndex > 0) {
      const prevContent = (module.content as any[])[contentIndex - 1];
      return isContentCompleted(moduleId, prevContent.id);
    }
    
    // Can access first content of module if previous module is completed
    const prevModule = course.modules.find((m: any) => m.order === module.order - 1);
    if (!prevModule) return true;
    
    const prevModuleCompleted = Array.isArray(prevModule.content) ? 
      prevModule.content.every((content: any) => isContentCompleted(prevModule.id, content.id)) : false;
    
    return prevModuleCompleted;
  };

  const toggleModule = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  // Check if a quiz is currently in cooldown
  const isQuizInCooldown = (quizId: string): { inCooldown: boolean, endTime?: Date } => {
    const savedCooldown = localStorage.getItem(`quiz_cooldown_${quizId}`);
    const sessionCooldown = sessionStorage.getItem(`quiz_cooldown_${quizId}`);
    const browserCooldown = localStorage.getItem(`quiz_global_cooldown_${quizId}`);
    
    // Use the latest cooldown time from any storage
    const cooldownTimes = [savedCooldown, sessionCooldown, browserCooldown]
      .filter(Boolean)
      .map(time => new Date(time!));
    
    if (cooldownTimes.length > 0) {
      const latestCooldown = new Date(Math.max(...cooldownTimes.map(d => d.getTime())));
      if (latestCooldown > new Date()) {
        return { inCooldown: true, endTime: latestCooldown };
      } else {
        // Clear all expired cooldowns
        localStorage.removeItem(`quiz_cooldown_${quizId}`);
        sessionStorage.removeItem(`quiz_cooldown_${quizId}`);
        localStorage.removeItem(`quiz_global_cooldown_${quizId}`);
      }
    }
    return { inCooldown: false };
  };

  const startQuiz = (quiz: any) => {
    // Check if quiz is in cooldown before allowing access
    const cooldownStatus = isQuizInCooldown(quiz.id);
    if (cooldownStatus.inCooldown && cooldownStatus.endTime) {
      const timeLeft = Math.max(0, Math.floor((cooldownStatus.endTime.getTime() - new Date().getTime()) / 1000));
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      toast.error(`Quiz is in cooldown. Please wait ${minutes}:${seconds.toString().padStart(2, '0')} before retaking.`);
      return;
    }
    
    setCurrentQuiz(quiz);
    setCurrentContentData(quiz);
    setCurrentViewMode('quiz');
    setQuizAnswers({});
    // Scroll to top when starting quiz
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startContent = (content: any, moduleId: string) => {
    setActiveContent(content.id);
    setActiveModule(moduleId);
    setCurrentContentData(content);
    setCurrentViewMode('content');
    // Scroll to top when starting new content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startAssignment = (assignment: any, moduleId: string) => {
    setActiveModule(moduleId);
    setCurrentContentData(assignment);
    setCurrentViewMode('assignment');
    // Scroll to top when starting assignment
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showCourseRating = () => {
    setCurrentViewMode('rating');
    // Scroll to top when showing rating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToOverview = () => {
    setCurrentViewMode('overview');
    setCurrentContentData(null);
    setCurrentQuiz(null);
    // Scroll to top when going back to overview
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitQuiz = () => {
    const score = Math.floor(Math.random() * 30) + 70; // Mock score 70-100
    markContentComplete(activeModule, currentQuiz.id);
    
    if (!enrollment) return;
    const newProgress = enrollment && Array.isArray(enrollment.progress) ? [...enrollment.progress] : [];
    const quizIndex = newProgress.findIndex(p => p.moduleId === activeModule && p.contentId === currentQuiz.id);
    if (quizIndex >= 0) {
      newProgress[quizIndex].score = score;
    }
    
    setEnrollment({
      ...enrollment,
      progress: newProgress
    });
    
    backToOverview();
    toast.success(`Quiz completed! Score: ${score}%`);
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <PlayIcon className="h-5 w-5" />;
      case 'text':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'interactive':
        return <BeakerIcon className="h-5 w-5" />;
      case 'document':
        return <BookOpenIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  // Navigation helpers
  const getAllContent = () => {
    const allContent: any[] = [];
    course.modules.forEach((module: any) => {
      if (Array.isArray(module.content)) {
        module.content.forEach((content: any) => {
          allContent.push({
            ...content,
            moduleId: module.id,
            moduleTitle: module.title
          });
        });
      } else if (module.content && typeof module.content === 'object' && module.content.type) {
        // Handle single content object
        const contentId = module.content.type === 'lesson' ? `${module.id}-lesson` : module.id;
        allContent.push({
          id: contentId,
          title: module.content.data?.title || module.title,
          type: module.content.type,
          duration: 30, // Default duration
          moduleId: module.id,
          moduleTitle: module.title
        });
      }
      // Add quiz if exists
      if (module.quiz) {
        allContent.push({
          ...module.quiz,
          moduleId: module.id,
          moduleTitle: module.title,
          type: 'quiz'
        });
      }
    });
    return allContent;
  };

  const getCurrentContentIndex = () => {
    const allContent = getAllContent();
    // Check if we're in quiz mode
    if (currentViewMode === 'quiz' && currentQuiz) {
      return allContent.findIndex(content => content.id === currentQuiz.id);
    }
    // Check if we're in content mode
    if (currentViewMode === 'content' && activeContent) {
      return allContent.findIndex(content => content.id === activeContent);
    }
    // Default: find first incomplete content
    if (enrollment && Array.isArray(enrollment.progress)) {
      const incompleteIndex = allContent.findIndex(content => 
        !enrollment.progress.some((p: any) => p.contentId === content.id && p.completed)
      );
      if (incompleteIndex >= 0) return incompleteIndex;
    }
    return 0; // Default to first content
  };

  const navigateToNext = () => {
    const allContent = getAllContent();
    const currentIndex = getCurrentContentIndex();
    if (currentIndex < allContent.length - 1) {
      const nextContent = allContent[currentIndex + 1];
      if (nextContent.type === 'quiz') {
        startQuiz(nextContent);
      } else {
        startContent(nextContent, nextContent.moduleId);
      }
    }
  };

  const navigateToPrevious = () => {
    const allContent = getAllContent();
    const currentIndex = getCurrentContentIndex();
    if (currentIndex > 0) {
      const previousContent = allContent[currentIndex - 1];
      if (previousContent.type === 'quiz') {
        startQuiz(previousContent);
      } else {
        startContent(previousContent, previousContent.moduleId);
      }
    }
  };

  const canNavigateNext = () => {
    const currentIndex = getCurrentContentIndex();
    const allContent = getAllContent();
    return currentIndex < allContent.length - 1;
  };

  const canNavigatePrevious = () => {
    const currentIndex = getCurrentContentIndex();
    return currentIndex > 0;
  };

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Debug logging
  console.log('CourseDetailsPage - Course data:', course);
  console.log('CourseDetailsPage - Modules:', course.modules);
  if (course.modules && course.modules[0]) {
    console.log('CourseDetailsPage - First module:', course.modules[0]);
    console.log('CourseDetailsPage - First module content:', course.modules[0].content);
  }

  // Guard against corrupted course data
  if (!course || !Array.isArray(course.modules)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Data Corrupted</h2>
          <p className="text-gray-600 mb-4">
            This course has corrupted data and needs to be recreated.
          </p>
          <p className="text-sm text-gray-500">
            Please contact an administrator to recreate this course from the template.
          </p>
        </div>
      </div>
    );
  }

  const activeModuleData = course.modules.find((m: any) => m.id === activeModule);
  const activeContentData = (Array.isArray(activeModuleData?.content)) ? 
    activeModuleData.content.find((c: any) => c.id === activeContent) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary-600 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/courses"
                className="flex items-center text-white hover:text-gray-900 mr-4"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Courses
              </Link>
              <h1 className="text-xl font-semibold text-white truncate">
                {course.title}
              </h1>
            </div>
            {isEnrolled && enrollment && (
              <div className="flex items-center space-x-4">
                <div className="text-sm text-white">
                  Progress: {enrollment.overallProgress}%
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${enrollment.overallProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Course Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              {/* Course Info */}
              <div className="mb-6">
                <img
                  src={course.thumbnailUrl || '/images/courses/react.jpg'}
                  alt={course.title}
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <h2 className="text-lg font-bold text-gray-900 mb-2">{course.title}</h2>
                <p className="text-sm text-gray-600 mb-4">{course.shortDescription}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Difficulty:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    course.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                    course.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {course.difficulty}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Price:</span>
                  {course.price === 0 ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      FREE
                    </span>
                  ) : (
                    <span className="font-semibold text-gray-900">${course.price}</span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Duration:</span>
                  <span>{course.duration} hours</span>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <span>Students:</span>
                  <span>{course.enrollmentCount.toLocaleString()}</span>
                </div>

                {/* Enrollment specific info */}
                {isEnrolled && enrollment && (
                  <div className="border-t border-gray-200 pt-4 mb-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Your Progress:</span>
                      <span className="font-semibold text-indigo-600">
                        {Math.round(enrollment.overallProgress || 0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${enrollment.overallProgress || 0}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Enrolled:</span>
                      <span>{enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString() : 'Recently'}</span>
                    </div>
                    {enrollment.quizScores && Object.keys(enrollment.quizScores).length > 0 && (
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Quiz Average:</span>
                        <span className="font-medium text-green-600">
                          {Math.round(
                            Object.values(enrollment.quizScores).reduce((sum: number, score: any) => sum + score, 0) /
                            Object.values(enrollment.quizScores).length
                          )}%
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.floor(course.averageRating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    {course.averageRating} ({course.reviewCount} reviews)
                  </span>
                </div>

                {!isEnrolled ? (
                  <button
                    onClick={enrollInCourse}
                    className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    {course.price === 0 ? 'Enroll for Free' : `Enroll Now - $${course.price}`}
                  </button>
                ) : (
                  <div className="space-y-4">
                    {/* Enrollment Status */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-center mb-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">Enrolled</span>
                      </div>
                      <div className="text-center text-sm text-green-700">
                        Started {enrollment?.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString() : 'Recently'}
                      </div>
                    </div>

                    {/* Progress Stats */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <h4 className="font-medium text-gray-900 text-center mb-3">Your Progress</h4>
                      
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-indigo-600">
                            {Math.round(enrollment?.overallProgress || 0)}%
                          </div>
                          <div className="text-xs text-gray-600">Complete</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-blue-600">
                            {enrollment && Array.isArray(enrollment.progress) ? enrollment.progress.filter((p: any) => p.completed).length : 0}
                          </div>
                          <div className="text-xs text-gray-600">Items Done</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-green-600">
                            {course.modules.filter((module: any) => isModuleCompleted(module)).length || 0}
                          </div>
                          <div className="text-xs text-gray-600">Modules</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-purple-600">
                            {enrollment?.quizScores && Object.keys(enrollment.quizScores).length > 0 
                              ? Math.round(
                                  Object.values(enrollment.quizScores).reduce((sum: number, score: any) => sum + score, 0) /
                                  Object.values(enrollment.quizScores).length
                                )
                              : 0}%
                          </div>
                          <div className="text-xs text-gray-600">Avg Score</div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          if (course.modules.length > 0) {
                            setActiveModule(course.modules[0].id);
                            if (Array.isArray(course.modules[0].content) && course.modules[0].content.length > 0) {
                              setActiveContent((course.modules[0].content as any[])[0].id);
                              // Content handled by new inline interface
                            } else if (course.modules[0].content && typeof course.modules[0].content === 'object' && course.modules[0].content.type) {
                              const contentId = course.modules[0].content.type === 'lesson' ? `${course.modules[0].id}-lesson` : course.modules[0].id;
                              setActiveContent(contentId);
                            }
                          }
                        }}
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                      >
                        Continue Learning
                      </button>
                      
                      {enrollment?.overallProgress === 100 && (
                        <button
                          onClick={() => showCourseRating()}
                          className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-yellow-700 transition-colors"
                        >
                          Rate Course
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Course Modules */}
              {isEnrolled && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Course Content</h3>
                  <div className="space-y-2">
                    {course.modules.map((module: any, moduleIndex: number) => (
                      <div key={module.id} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                        >
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {module.order}. {module.title}
                            </span>
                          </div>
                          {expandedModules.has(module.id) ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                        
                        {expandedModules.has(module.id) && (
                          <div className="border-t border-gray-200">
                            {Array.isArray(module.content) ? (
                              console.log(`Module ${module.id} content is array:`, module.content), 
                              module.content.map((content: any, contentIndex: number) => {
                              const canAccess = canAccessContent(module.id, contentIndex);
                              const isCompleted = isContentCompleted(module.id, content.id);
                              const isActive = activeContent === content.id;
                              
                              return (
                                <button
                                  key={content.id}
                                  onClick={() => {
                                    if (canAccess) {
                                      startContent(content, module.id);
                                    }
                                  }}
                                  disabled={!canAccess}
                                  className={`w-full flex items-center p-3 text-left text-sm transition-colors ${
                                    !canAccess
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : isActive
                                      ? 'bg-indigo-50 text-indigo-900'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center flex-1">
                                    <div className="mr-3">
                                      {!canAccess ? (
                                        <LockClosedIcon className="h-4 w-4 text-gray-400" />
                                      ) : isCompleted ? (
                                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                      ) : (
                                        getContentIcon(content.type)
                                      )}
                                    </div>
                                    <span className={isCompleted ? 'line-through' : ''}>
                                      {content.title}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {content.duration} min
                                  </span>
                                </button>
                              );
                            })
                            ) : module.content && typeof module.content === 'object' && module.content.type ? (
                              // Handle single content object
                              console.log(`Module ${module.id} content is object:`, module.content),
                              (() => {
                                const content = {
                                  id: module.content.type === 'lesson' ? `${module.id}-lesson` : module.id,
                                  title: module.content.data?.title || module.title,
                                  type: module.content.type,
                                  duration: 30 // Default duration
                                };
                                const canAccess = true; // First module content is always accessible
                                const isCompleted = isContentCompleted(module.id, content.id);
                                const isActive = activeContent === content.id;
                                
                                return (
                                  <button
                                    key={content.id}
                                    onClick={() => {
                                      startContent(content, module.id);
                                    }}
                                    className={`w-full flex items-center p-3 text-left text-sm transition-colors ${
                                      isActive
                                        ? 'bg-indigo-50 text-indigo-900'
                                        : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center flex-1">
                                      <div className="mr-3">
                                        {isCompleted ? (
                                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                        ) : content.type === 'lesson' ? (
                                          <BookOpenIcon className="h-4 w-4 text-gray-500" />
                                        ) : (
                                          <PlayIcon className="h-4 w-4 text-gray-500" />
                                        )}
                                      </div>
                                      <span className={isCompleted ? 'line-through' : ''}>
                                        {content.title}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {content.duration} min
                                    </span>
                                  </button>
                                );
                              })()
                            ) : (
                              console.log(`Module ${module.id} content is unknown format:`, module.content), null
                            )}
                            
                            {/* Module Quiz */}
                            {module.quiz && (
                              <button
                                onClick={() => {
                                  setActiveModule(module.id);
                                  startQuiz(module.quiz);
                                }}
                                disabled={
                                  !isModuleCompleted(module) ||
                                  isQuizInCooldown(module.quiz.id).inCooldown
                                }
                                className={`w-full flex items-center p-3 text-left text-sm border-t transition-colors ${
                                  !isModuleCompleted(module)
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : isQuizInCooldown(module.quiz.id).inCooldown
                                    ? 'text-red-400 cursor-not-allowed bg-red-50'
                                    : isContentCompleted(module.id, module.quiz.id)
                                    ? 'bg-green-50 text-green-900'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center flex-1">
                                  <div className="mr-3">
                                    {!isModuleCompleted(module) ? (
                                      <LockClosedIcon className="h-4 w-4 text-gray-400" />
                                    ) : isQuizInCooldown(module.quiz.id).inCooldown ? (
                                      <ClockIcon className="h-4 w-4 text-red-500" />
                                    ) : isContentCompleted(module.id, module.quiz.id) ? (
                                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <PuzzlePieceIcon className="h-4 w-4 text-blue-500" />
                                    )}
                                  </div>
                                  <div className="flex flex-col">
                                    <span>Quiz: {module.quiz.title}</span>
                                    {isQuizInCooldown(module.quiz.id).inCooldown && isQuizInCooldown(module.quiz.id).endTime && (
                                      <SidebarCooldownTimer endTime={isQuizInCooldown(module.quiz.id).endTime!} />
                                    )}
                                  </div>
                                </div>
                                {isContentCompleted(module.id, module.quiz.id) && (
                                  <span className="text-xs text-green-600">
                                    {enrollment && Array.isArray(enrollment.progress) ? enrollment.progress.find(p => p.moduleId === module.id && p.contentId === module.quiz.id)?.score : 0}%
                                  </span>
                                )}
                              </button>
                            )}

                            {/* Module Assignment */}
                            {module.assignment && (
                              <button
                                onClick={() => startAssignment(module.assignment, module.id)}
                                className="w-full flex items-center p-3 text-left text-sm border-t text-gray-700 hover:bg-gray-50"
                              >
                                <div className="flex items-center flex-1">
                                  <AcademicCapIcon className="h-4 w-4 text-purple-500 mr-3" />
                                  <span>Assignment: {module.assignment.title}</span>
                                </div>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {!isEnrolled ? (
              /* Course Preview */
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.title}</h1>
                  <p className="text-lg text-gray-600 mb-6">{course.description}</p>
                  
                  <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mb-8">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      {course.duration} hours
                    </div>
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 mr-2" />
                      {course.enrollmentCount.toLocaleString()} students
                    </div>
                    <div className="flex items-center">
                      <StarIcon className="h-5 w-5 mr-2 text-yellow-400" />
                      {course.averageRating}/5.0
                    </div>
                  </div>

                  <button
                    onClick={enrollInCourse}
                    className="bg-indigo-600 text-white py-4 px-8 rounded-lg font-semibold text-lg hover:bg-indigo-700 transition-colors"
                  >
                    {course.price === 0 ? 'Enroll for Free' : `Enroll Now - $${course.price}`}
                  </button>
                </div>

                {/* What You'll Learn */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">What You'll Learn</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {course.learningObjectives.map((objective: string, index: number) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{objective}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Course Modules Preview */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Content</h2>
                  <div className="space-y-4">
                    {course.modules.map((module: any, index: number) => (
                      <div key={module.id} className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Module {module.order}: {module.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-3">{module.description}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {module.estimatedHours} hours • {getContentCount(module)} lessons
                          {module.quiz && ' • 1 quiz'}
                          {module.assignment && ' • 1 assignment'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Course Learning Interface */
              <div className="bg-white rounded-lg shadow-md">
                {currentViewMode === 'overview' ? (
                  /* Course Overview for Enrolled Users */
                  <div className="p-6">
                    <div className="mb-6">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h1>
                      <p className="text-gray-600">{course.description}</p>
                    </div>

                    {/* Progress Summary */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 mb-6">
                      <h3 className="font-semibold text-gray-900 mb-4">Your Progress</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-indigo-600">
                            {Math.round(enrollment?.overallProgress || 0)}%
                          </div>
                          <div className="text-sm text-gray-600">Complete</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {enrollment && Array.isArray(enrollment.progress) ? enrollment.progress.filter((p: any) => p.completed).length : 0}
                          </div>
                          <div className="text-sm text-gray-600">Items Done</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {course.modules.filter((module: any) =>
                              isModuleCompleted(module)
                            ).length || 0}
                          </div>
                          <div className="text-sm text-gray-600">Modules</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {enrollment?.quizScores && Object.keys(enrollment.quizScores).length > 0
                              ? Math.round(
                                  Object.values(enrollment.quizScores).reduce((sum: number, score: any) => sum + score, 0) /
                                  Object.values(enrollment.quizScores).length
                                )
                              : 0}%
                          </div>
                          <div className="text-sm text-gray-600">Avg Score</div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {getAllContent().length > 0 && (
                        <button
                          onClick={() => {
                            const allContent = getAllContent();
                            let targetContent = null;
                            
                            // Find first incomplete content
                            if (enrollment && Array.isArray(enrollment.progress)) {
                              targetContent = allContent.find(content => 
                                !enrollment.progress.some((p: any) => p.contentId === content.id && p.completed)
                              );
                            }
                            
                            // If no incomplete content, start with first content
                            if (!targetContent && allContent.length > 0) {
                              targetContent = allContent[0];
                            }
                            
                            if (targetContent) {
                              if (targetContent.type === 'quiz') {
                                startQuiz(targetContent);
                              } else {
                                startContent(targetContent, targetContent.moduleId);
                              }
                            }
                          }}
                          className="flex items-center p-4 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          <PlayIcon className="h-8 w-8 text-indigo-600 mr-3" />
                          <div className="text-left">
                            <div className="font-medium text-gray-900">Continue Learning</div>
                            <div className="text-sm text-gray-600">Resume your progress</div>
                          </div>
                        </button>
                      )}
                      
                      {enrollment?.overallProgress === 100 && (
                        <button
                          onClick={showCourseRating}
                          className="flex items-center p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <StarIcon className="h-8 w-8 text-green-600 mr-3" />
                          <div className="text-left">
                            <div className="font-medium text-gray-900">Rate Course</div>
                            <div className="text-sm text-gray-600">Share your experience</div>
                          </div>
                        </button>
                      )}
                    </div>

                    {/* Recent Activity or Next Steps */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">What's Next</h4>
                      <div className="space-y-2">
                        {course.modules.slice(0, 3).map((module: any) => {
                          const moduleComplete = isModuleCompleted(module);
                          const isExpanded = expandedModules.has(module.id);
                          
                          return (
                            <div key={module.id} className="bg-gray-50 rounded-lg overflow-hidden">
                              <div className="flex items-center p-3">
                                {moduleComplete ? (
                                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                ) : (
                                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                                )}
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{module.title}</div>
                                  <div className="text-xs text-gray-600">{module.estimatedHours} hours</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {!moduleComplete && (
                                    <button
                                      onClick={() => {
                                        // Find first incomplete content in this module
                                        let firstIncompleteContent = null;
                                        if (Array.isArray(module.content)) {
                                          firstIncompleteContent = module.content.find((content: any) =>
                                            !enrollment || !Array.isArray(enrollment.progress) || 
                                            !enrollment.progress.some((p: any) => p.contentId === content.id && p.completed)
                                          );
                                        } else if (module.content && typeof module.content === 'object' && module.content.type) {
                                          const contentId = module.content.type === 'lesson' ? `${module.id}-lesson` : module.id;
                                          if (!isContentCompleted(module.id, contentId)) {
                                            firstIncompleteContent = {
                                              id: contentId,
                                              title: module.content.data?.title || module.title,
                                              type: module.content.type
                                            };
                                          }
                                        }
                                        
                                        if (firstIncompleteContent) {
                                          startContent(firstIncompleteContent, module.id);
                                        } else if (module.quiz && (!enrollment || !Array.isArray(enrollment.progress) || 
                                          !enrollment.progress.some((p: any) => p.contentId === module.quiz.id && p.completed))) {
                                          startQuiz(module.quiz);
                                        }
                                      }}
                                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                                    >
                                      Start
                                    </button>
                                  )}
                                  <button
                                    onClick={() => toggleModule(module.id)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              
                              {/* Expanded Content */}
                              {isExpanded && (
                                <div className="border-t border-gray-200 bg-white">
                                  {Array.isArray(module.content) ? module.content.map((content: any, contentIndex: number) => {
                                    const canAccess = canAccessContent(module.id, contentIndex);
                                    const isCompleted = isContentCompleted(module.id, content.id);
                                    
                                    return (
                                      <button
                                        key={content.id}
                                        onClick={() => {
                                          if (canAccess) {
                                            startContent(content, module.id);
                                          }
                                        }}
                                        disabled={!canAccess}
                                        className={`w-full flex items-center p-3 text-left text-sm transition-colors ${
                                          !canAccess
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : isCompleted
                                            ? 'text-green-700 bg-green-50'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                      >
                                        <div className="flex items-center flex-1">
                                          <div className="mr-3">
                                            {!canAccess ? (
                                              <LockClosedIcon className="h-4 w-4 text-gray-400" />
                                            ) : isCompleted ? (
                                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                            ) : (
                                              getContentIcon(content.type)
                                            )}
                                          </div>
                                          <span className={isCompleted ? 'line-through' : ''}>
                                            {content.title}
                                          </span>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                          {content.duration} min
                                        </span>
                                      </button>
                                    );
                                  }) : module.content && typeof module.content === 'object' && module.content.type ? (
                                    // Handle single content object
                                    (() => {
                                      const content = {
                                        id: module.content.type === 'lesson' ? `${module.id}-lesson` : module.id,
                                        title: module.content.data?.title || module.title,
                                        type: module.content.type
                                      };
                                      const isCompleted = isContentCompleted(module.id, content.id);
                                      
                                      return (
                                        <button
                                          key={content.id}
                                          onClick={() => {
                                            startContent(content, module.id);
                                          }}
                                          className={`w-full flex items-center p-3 text-left text-sm transition-colors ${
                                            isCompleted
                                              ? 'text-green-700 bg-green-50'
                                              : 'text-gray-700 hover:bg-gray-50'
                                          }`}
                                        >
                                          <div className="flex items-center flex-1">
                                            <div className="mr-3">
                                              {isCompleted ? (
                                                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                              ) : content.type === 'lesson' ? (
                                                <BookOpenIcon className="h-4 w-4 text-gray-500" />
                                              ) : (
                                                <PlayIcon className="h-4 w-4 text-gray-500" />
                                              )}
                                            </div>
                                            <span>{content.title}</span>
                                          </div>
                                        </button>
                                      );
                                    })()
                                  ) : null}
                                  
{/* Module Quiz */}
                                  {module.quiz && (
                                    <button
                                      onClick={() => {
                                        setActiveModule(module.id);
                                        startQuiz(module.quiz);
                                      }}
                                      disabled={
                                        !isModuleCompleted(module) ||
                                        isQuizInCooldown(module.quiz.id).inCooldown
                                      }
                                      className={`w-full flex items-center p-3 text-left text-sm border-t transition-colors ${
                                        !isModuleCompleted(module)
                                          ? 'text-gray-400 cursor-not-allowed'
                                          : isQuizInCooldown(module.quiz.id).inCooldown
                                          ? 'text-red-400 cursor-not-allowed bg-red-50'
                                          : isContentCompleted(module.id, module.quiz.id)
                                          ? 'bg-green-50 text-green-900'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <div className="flex items-center flex-1">
                                        <div className="mr-3">
                                          {!isModuleCompleted(module) ? (
                                            <LockClosedIcon className="h-4 w-4 text-gray-400" />
                                          ) : isQuizInCooldown(module.quiz.id).inCooldown ? (
                                            <ClockIcon className="h-4 w-4 text-red-500" />
                                          ) : isContentCompleted(module.id, module.quiz.id) ? (
                                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                          ) : (
                                            <PuzzlePieceIcon className="h-4 w-4 text-blue-500" />
                                          )}
                                        </div>
                                        <div className="flex flex-col">
                                          <span>Quiz: {module.quiz.title}</span>
                                          {isQuizInCooldown(module.quiz.id).inCooldown && isQuizInCooldown(module.quiz.id).endTime && (
                                            <SidebarCooldownTimer endTime={isQuizInCooldown(module.quiz.id).endTime!} />
                                          )}
                                        </div>
                                      </div>
                                      {isContentCompleted(module.id, module.quiz.id) && (
                                        <span className="text-xs text-green-600">
                                          {enrollment && Array.isArray(enrollment.progress) ? enrollment.progress.find(p => p.moduleId === module.id && p.contentId === module.quiz.id)?.score : 0}%
                                        </span>
                                      )}
                                    </button>
                                  )}

                                  {/* Module Assignment */}
                                  {module.assignment && (
                                    <button
                                      onClick={() => startAssignment(module.assignment, module.id)}
                                      className="w-full flex items-center p-3 text-left text-sm border-t text-gray-700 hover:bg-gray-50"
                                    >
                                      <div className="flex items-center flex-1">
                                        <AcademicCapIcon className="h-4 w-4 text-purple-500 mr-3" />
                                        <span>Assignment: {module.assignment.title}</span>
                                      </div>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : currentViewMode === 'content' && currentContentData ? (
                  /* Content Viewer */
                  <div>
                    {/* Back Button */}
                    <div className="p-4 border-b border-gray-200">
                      <button
                        onClick={backToOverview}
                        className="flex items-center text-indigo-600 hover:text-indigo-700"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Course
                      </button>
                    </div>
                    
                    <ContentViewer
                      content={currentContentData}
                      courseId={courseId!}
                      moduleId={activeModule}
                      isCompleted={isContentCompleted(activeModule, activeContent)}
                      onComplete={() => markContentComplete(activeModule, activeContent)}
                      onNext={navigateToNext}
                      onPrevious={navigateToPrevious}
                      canNavigateNext={canNavigateNext()}
                      canNavigatePrevious={canNavigatePrevious()}
                    />
                  </div>
                ) : currentViewMode === 'quiz' && currentQuiz ? (
                  /* Quiz Interface */
                  <div className="h-full">
                    {/* Back Button */}
                    <div className="p-4 border-b border-gray-200">
                      <button
                        onClick={backToOverview}
                        className="flex items-center text-indigo-600 hover:text-indigo-700"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Course
                      </button>
                    </div>
                    
                    {/* Quiz takes full height without additional padding */}
                    <QuizComponent
                      quiz={currentQuiz}
                      courseId={courseId!}
                      moduleId={activeModule}
                      onComplete={(score, passed) => {
                        if (passed) {
                          markContentComplete(activeModule, currentQuiz.id);
                        }
                        backToOverview();
                      }}
                      onClose={backToOverview}
                    />
                  </div>
                ) : currentViewMode === 'assignment' && currentContentData ? (
                  /* Assignment Interface */
                  <div>
                    {/* Back Button */}
                    <div className="p-4 border-b border-gray-200">
                      <button
                        onClick={backToOverview}
                        className="flex items-center text-indigo-600 hover:text-indigo-700"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Course
                      </button>
                    </div>
                    
                    <div className="p-6">
                      <AssignmentComponent
                        assignment={currentContentData}
                        courseId={courseId!}
                        moduleId={activeModule}
                        onComplete={() => {
                          markContentComplete(activeModule, currentContentData.id);
                          backToOverview();
                        }}
                      />
                    </div>
                  </div>
                ) : currentViewMode === 'rating' ? (
                  /* Course Rating */
                  <div>
                    {/* Back Button */}
                    <div className="p-4 border-b border-gray-200">
                      <button
                        onClick={backToOverview}
                        className="flex items-center text-indigo-600 hover:text-indigo-700"
                      >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Course
                      </button>
                    </div>
                    
    <div className="p-6">
                      <CourseRating
                        courseId={courseId!}
                        onRatingSubmitted={(rating, review) => {
                          backToOverview();
                          toast.success('Thank you for rating this course!');
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsPage; 